import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { MailApi } from '@/api/contract';
import { ApiError } from '@/api/http';
import { ApiErrorCodes } from '@/api/errors';
import { queryKeys } from '@/api/queryKeys';
import { useApi } from '@/api/provider';
import type { AccountLimits, NextcloudStatus } from '@/api/types';
import { MAX_MESSAGE_ATTACHMENT_BYTES } from '@/features/outbox/types';

/**
 * Whether Nextcloud is there for the composer, and what it says (ADR 0084).
 *
 * `unavailable` covers the deployment without the feature (`501
 * unsupported_by_host`) and any doubt at all -- a status that could not be
 * read draws no Nextcloud UI, as the AI assistant draws none (ADR 0065).
 */
export interface NextcloudAvailability {
  status: NextcloudStatus | undefined;
  /** The feature is off here, or its status could not be read. */
  unavailable: boolean;
  /** An active link: uploads and the picker work. */
  linked: boolean;
}

const STALE_MS = 60_000;

function query(api: MailApi) {
  return {
    queryKey: queryKeys.nextcloud(),
    queryFn: ({ signal }: { signal: AbortSignal }) => api.nextcloud.getNextcloud(signal),
    retry: false,
    staleTime: STALE_MS,
  } as const;
}

export function isUnsupported(error: unknown): boolean {
  return error instanceof ApiError && (error.is(ApiErrorCodes.UnsupportedByHost) || error.status === 501);
}

export function useNextcloudStatus(enabled = true): NextcloudAvailability {
  const api = useApi();
  const result = useQuery({ ...query(api), enabled });
  const status = result.data;
  return {
    status,
    unavailable: result.isError || (status === undefined && !result.isPending),
    linked: status?.state === 'active',
  };
}

/**
 * The status at attach time: the cached one, or one fetched now. `undefined`
 * is "unavailable" -- 501, or it could not be read -- and the composer then
 * behaves as it did before Nextcloud.
 */
export async function fetchNextcloudStatus(
  queryClient: QueryClient,
  api: MailApi,
): Promise<NextcloudStatus | undefined> {
  try {
    return await queryClient.fetchQuery(query(api));
  } catch {
    return undefined;
  }
}

/**
 * The account's attachment ceilings (D7), or the constant the client used to
 * guess with when the server names none.
 */
export function attachmentLimits(limits: AccountLimits | undefined): {
  maxAttachmentBytes: number;
  maxAttachmentTotalBytes: number;
} {
  return {
    maxAttachmentBytes: limits?.maxAttachmentBytes ?? MAX_MESSAGE_ATTACHMENT_BYTES,
    maxAttachmentTotalBytes: limits?.maxAttachmentTotalBytes ?? MAX_MESSAGE_ATTACHMENT_BYTES,
  };
}

/** What happens to one file the moment it is added to the composer (D7). */
export type AttachDecision =
  /** Upload it as an ordinary attachment, as always. */
  | { kind: 'attach' }
  /** Nextcloud is not here, and the file cannot be attached: refused before a byte moves. */
  | { kind: 'refuse'; reason: 'file_too_large' | 'total_too_large' }
  /**
   * The offer row. `attachAnyway` is false when the mail server would refuse
   * the file (over the cap, or the message total).
   */
  | { kind: 'offer'; attachAnyway: boolean };

export interface DecideInput {
  sizeBytes: number;
  /** Bytes already in the message's attachments. */
  totalBytes: number;
  limits: { maxAttachmentBytes: number; maxAttachmentTotalBytes: number };
  /** `undefined`: Nextcloud unavailable (501, or unreadable). */
  status: NextcloudStatus | undefined;
}

/**
 * D7's table:
 *
 * | file                                  | Nextcloud here           | unavailable |
 * |---------------------------------------|--------------------------|-------------|
 * | ≤ threshold, fits                     | attach                   | attach      |
 * | > threshold, ≤ cap, total fits        | offer + *Attach anyway*  | attach      |
 * | > cap, or the total would not fit     | offer, no *Attach anyway*| refuse      |
 *
 * Whether the offer says "upload" or "link a Nextcloud account first" is the
 * link's state, read when the row is drawn -- linking in another tab turns
 * the one into the other without re-adding the file.
 */
export function decideAttach({ sizeBytes, totalBytes, limits, status }: DecideInput): AttachDecision {
  const overFile = sizeBytes > limits.maxAttachmentBytes;
  const overTotal = totalBytes + sizeBytes > limits.maxAttachmentTotalBytes;
  if (status === undefined) {
    if (overFile) return { kind: 'refuse', reason: 'file_too_large' };
    if (overTotal) return { kind: 'refuse', reason: 'total_too_large' };
    return { kind: 'attach' };
  }
  if (overFile || overTotal) return { kind: 'offer', attachAnyway: false };
  if (sizeBytes <= status.settings.linkThresholdBytes) return { kind: 'attach' };
  return { kind: 'offer', attachAnyway: true };
}
